import h from 'hyperscript';
import stopwatchIcon from '@disy/cadenza-icons/routing-mini-legend-stopwatch.svg';
import distanceIcon from '@disy/cadenza-icons/routing-distance.svg';
import upIcon from '@disy/cadenza-icons/routing-up.svg';
import downIcon from '@disy/cadenza-icons/routing-down.svg';

import { formatDuration, formatLength } from 'cadenza/utils/format/format';
import { icon } from 'cadenza/utils/icon/icon';
import i18n from 'cadenza/map/routing/routing-sidebar/route/route.properties';

export const ROUTE_ATTRIBUTES_ICONS: Record<keyof StepValues, string> = {
  duration: stopwatchIcon,
  distance: distanceIcon,
  ascent: upIcon,
  descent: downIcon
};

const formatElevation = (value: number) => formatLength(value, true);

export const ROUTE_ATTRIBUTES_FORMATTERS: Record<keyof StepValues, (val: number) => string> = {
  duration: formatDurationForDirections,
  distance: formatLength,
  ascent: formatElevation,
  descent: formatElevation
};

const stepProperties = [ 'duration', 'distance', 'ascent', 'descent' ] as const;
type StepProps = typeof stepProperties[number];

type StepValues = {
  [Property in StepProps]?: number;
};

export function createValuesElement (values: StepValues) {
  const valueElements: HTMLElement[] = [];
  for (const key of stepProperties) {
    const value = values[key];
    if (value == null) {
      continue;
    }

    const valueIcon = ROUTE_ATTRIBUTES_ICONS[key];
    const formattedValue = ROUTE_ATTRIBUTES_FORMATTERS[key](value);
    valueElements.push(h('.d-route--value', [
      h('span.d-route--value--icon', icon(valueIcon)),
      h('span.d-route--value--label', formattedValue)
    ]));
  }

  return h('.d-route--values', valueElements);
}

const _1_MINUTE_IN_SECONDS = 60;
const _1_HOUR_IN_SECONDS = 60 * 60;

/**
 * Formats time duration for the route directions to h, min, s format (for example "1h 30min", "1h", "30min 5s")
 * Seconds are included only if the duration is less than 1 hour and seconds value is not 0.
 * If the duration is less than 1 minute, a string '< 1 min' is returned. *
 *
 * @param {number} durationInSeconds - The duration for format in seconds
 * @return {string} The formatted duration
 * @example
 * formatDurationForDirections(5 * 60); // returns '5min'
 * formatDurationForDirections(60 * 60); // returns '1h'
 * formatDurationForDirections(65 * 60); // returns '1h 5min'
 * formatDurationForDirections(3.5 * 60); // returns '3min 30s'
 * formatDurationForDirections(63.5 * 60); // returns '1h 3min'
 */
export function formatDurationForDirections (durationInSeconds: number) {
  if (durationInSeconds < _1_MINUTE_IN_SECONDS) {
    return i18n('minutes', { minutes: '< 1' });
  }
  return formatDuration(durationInSeconds * 1000, {
    includeSeconds: durationInSeconds < _1_HOUR_IN_SECONDS
  });
}
